import type { LocalStorageService } from './services/browser/LocalStorageService';
import { StorageKey } from './config/storageKeys';

/** Query parameter carried by BirdsView email links (written by the INT-293 send path). */
export const MAIL_SEED_PARAM = 'bv_cid';

/**
 * 30 days. Proportionality bound on an identifier written to device storage before consent,
 * weighed against the 12-month raw-retention decision; `device_customer_map.deviceFallbackDays`
 * (default 90) is the server-side precedent. Refreshed on every new click.
 */
export const MAIL_SEED_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const SEED_ID_MAX_LENGTH = 64;
const SEED_ID_PATTERN = /^[A-Za-z0-9-]+$/;

interface StoredSeed {
  id: string;
  at: number;
}

interface CaptureDeps {
  href: string;
  replaceUrl(url: string): void;
  storage: LocalStorageService;
  now?: number;
}

/**
 * `'rejected'` exists so the caller can log it. The producer of `bv_cid` (INT-293) is not built yet,
 * so a charset mismatch between the two is a live risk — and its only symptom would otherwise be
 * "attribution silently never fires", with nothing in the debug stream to point at.
 */
export type CaptureResult = 'stored' | 'rejected' | 'absent';

/**
 * The module's single storage guard. `BrowserLocalStorageService` is unguarded and throws when site
 * data is blocked, and the read path runs once per heartbeat — so the catch lives here rather than
 * at each caller. A corrupt value is reported as absent but deliberately left in place: the next
 * capture overwrites it and consent withdrawal removes it.
 */
function readStoredSeed(storage: LocalStorageService): StoredSeed | null {
  try {
    const raw = storage.get(StorageKey.MailSeed);
    if (raw == null) {
      return null;
    }

    // Cast at the parse site, matching `shopware66/injectedPage.ts` — an intermediate `unknown`
    // variable would trip `@typescript-eslint/no-unsafe-assignment`.
    const parsed = JSON.parse(raw) as { id?: unknown; at?: unknown };
    if (parsed == null || typeof parsed !== 'object') {
      return null;
    }

    const { id, at } = parsed;
    if (typeof id !== 'string' || typeof at !== 'number' || !Number.isFinite(at)) {
      return null;
    }

    return { id, at };
  } catch {
    return null;
  }
}

/** Bounding only — the value is opaque (it may be a newsletter-recipient id, not a Shopware customer id). */
function isValidSeedId(value: string): boolean {
  return value.length <= SEED_ID_MAX_LENGTH && SEED_ID_PATTERN.test(value);
}

/**
 * A timestamp ahead of `now` counts as expired: a device whose clock was fast when the seed was
 * written (or a hand-edited `at`) would otherwise never satisfy `now - at > TTL` and the identifier
 * would live forever.
 */
function isExpired(
  at: number,
  now: number
): boolean {
  return at > now || now - at > MAIL_SEED_TTL_MS;
}

/**
 * Removes one parameter by splicing the raw query string, NOT by re-serializing through
 * `URLSearchParams` — `url.searchParams.delete()` followed by `url.toString()` rewrites the encoding
 * of every *surviving* parameter (`%20` becomes `+`, `/` becomes `%2F`), which third-party snippets
 * that read `location.search` with a regex would see. The name is a fixed literal, so it needs no
 * encoding of its own.
 */
function stripQueryParam(
  href: string,
  name: string
): string {
  const hashAt = href.indexOf('#');
  const hash = hashAt === -1 ? '' : href.slice(hashAt);
  const head = hashAt === -1 ? href : href.slice(0, hashAt);

  const queryAt = head.indexOf('?');
  if (queryAt === -1) {
    return href;
  }

  const kept = head
    .slice(queryAt + 1)
    .split('&')
    .filter((pair) => pair !== name && !pair.startsWith(`${name}=`));
  const base = head.slice(0, queryAt);

  return kept.length > 0 ? `${base}?${kept.join('&')}${hash}` : `${base}${hash}`;
}

/**
 * Runs at script parse time, before any consent decision: consent gates the *use* of the id, not its
 * capture, and the parameter is gone from the URL by the time a visitor accepts a banner two pages
 * later. Also the only place an expired seed is purged, since the read path is pure.
 */
export function captureMailSeed(deps: CaptureDeps): CaptureResult {
  const now = deps.now ?? Date.now();
  const raw = new URL(deps.href).searchParams.get(MAIL_SEED_PARAM);

  // Unconditionally, before the parameter branch: a landing that carries a *malformed* `bv_cid`
  // returns below without storing, and would otherwise leave an already-expired seed in place.
  const stored = readStoredSeed(deps.storage);
  if (stored != null && isExpired(stored.at, now)) {
    deps.storage.remove(StorageKey.MailSeed);
  }

  if (raw == null) {
    return 'absent';
  }

  // Scrub before validating: a malformed value must not reach `page.url` either.
  deps.replaceUrl(stripQueryParam(deps.href, MAIL_SEED_PARAM));

  if (!isValidSeedId(raw)) {
    return 'rejected';
  }

  deps.storage.set(StorageKey.MailSeed, JSON.stringify({ id: raw, at: now }));

  return 'stored';
}

/** Pure read — no writes on the per-heartbeat hot path, and no ordering coupling with capture. */
export function readMailSeed(
  storage: LocalStorageService,
  now: number = Date.now(),
): string | null {
  const stored = readStoredSeed(storage);
  if (stored == null) {
    return null;
  }
  if (isExpired(stored.at, now)) {
    return null;
  }

  return stored.id;
}
